import { unwrapOrThrow } from "admin/api/api_result";
import { cachedGetPricingPlanStatus } from "admin/api/organization";
import { PlanAboutToExceedAlert, PlanExceededAlert } from "admin/organization/organization_cards";
import { getUser, updateNovelUserExperienceInfos } from "admin/rest_api";
import { WhatsNextHeader } from "admin/welcome_ui";
import { Spin, Tabs, type TabsProps, Typography } from "antd";
import AnnotationsView from "dashboard/annotations_view";
import { PublicationViewWithHeader } from "dashboard/publication_view";
import DashboardTaskListView from "dashboard/task_list/dashboard_task_list_view";
import features from "features";
import { useMinScreenWidth } from "libs/react_hooks";
import Request from "libs/request";
import UserLocalStorage from "libs/user_local_storage";
import { getUrlParamsObjectFromString } from "libs/utils";
import { type RouteComponentProps, withRouter } from "libs/with_router_hoc";
import invert from "lodash-es/invert";
import type React from "react";
import { PureComponent } from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import type { APIOrganization, APIPricingPlanStatus, APIUser } from "types/api_types";
import { enforceActiveOrganization } from "viewer/model/accessors/organization_accessors";
import { enforceActiveUser } from "viewer/model/accessors/user_accessor";
import { setActiveUserAction } from "viewer/model/actions/user_actions";
import type { WebknossosState } from "viewer/store";
import { PortalTarget } from "viewer/view/layouting/portal_utils";
import NmlUploadZoneContainer, {
  type NmlImportOptions,
} from "viewer/view/nml_upload/nml_upload_zone_container";
import { ActiveTabContext, RenderingTabContext } from "./dashboard_contexts";
import { DatasetFolderView } from "./dataset_folder_view";

type OwnProps = {
  userId: string | null | undefined;
  isAdminView: boolean;
  initialTabKey: string | null | undefined;
};
type StateProps = {
  activeUser: APIUser;
  activeOrganization: APIOrganization;
};
type DispatchProps = {
  updateActiveUser: (arg0: APIUser) => void;
};
type Props = OwnProps & StateProps & DispatchProps;
type PropsWithRouter = Props & RouteComponentProps;
type State = {
  activeTabKey: string;
  user: APIUser | null | undefined;
  organization: APIOrganization | null;
  pricingPlanStatus: APIPricingPlanStatus | null;
};

export const urlTokenToTabKeyMap = {
  publications: "publications",
  datasets: "datasets",
  tasks: "tasks",
  annotations: "annotations",
};

function TabBarExtraContent() {
  return (
    <PortalTarget
      portalId="dashboard-TabBarExtraContent"
      style={{
        flex: 1,
        display: "flex",
      }}
    />
  );
}

// The tabs render their toolbar (Refresh, Add Dataset, search, ...) into a portal. On wide screens,
// it sits next to the tabs. On narrow screens, it gets its own row below them: antd subtracts the
// extra content's width when deciding which tabs fit, so sharing the row would hide the tabs
// behind the "..." overflow menu, and the toolbar itself would overflow the page.
function DashboardTabs(props: Omit<TabsProps, "tabBarExtraContent" | "renderTabBar">) {
  // Must be correct on the first render: switching the layout afterwards would move the toolbar's
  // portal target and blur the autofocused search input.
  const isWideScreen = useMinScreenWidth("lg");
  if (isWideScreen) {
    return <Tabs {...props} tabBarExtraContent={<TabBarExtraContent />} />;
  }
  return (
    <Tabs
      {...props}
      renderTabBar={(tabBarProps, DefaultTabBar) => (
        <>
          <DefaultTabBar {...tabBarProps} />
          <div style={{ marginBottom: 16 }}>
            <TabBarExtraContent />
          </div>
        </>
      )}
    />
  );
}

type Tab = {
  label: string;
  key: string;
  children: React.ReactElement;
};

class DashboardView extends PureComponent<PropsWithRouter, State> {
  constructor(props: PropsWithRouter) {
    super(props);
    const validTabKeys = this.getValidTabKeys();
    const { initialTabKey } = this.props;
    const lastUsedTabKey = UserLocalStorage.getItem("lastUsedDashboardTab");
    // The admin view of another user's dashboard has no datasets tab.
    const defaultTabKey = this.props.isAdminView ? "annotations" : "datasets";

    const activeTabKey =
      (initialTabKey && validTabKeys[initialTabKey] ? initialTabKey : null) ||
      (lastUsedTabKey && validTabKeys[lastUsedTabKey] ? lastUsedTabKey : null) ||
      defaultTabKey;

    this.state = {
      activeTabKey,
      user: null,
      organization: null,
      pricingPlanStatus: null,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps: PropsWithRouter) {
    if (this.props.initialTabKey != null && prevProps.initialTabKey !== this.props.initialTabKey) {
      this.setState({
        activeTabKey: this.props.initialTabKey,
      });
    }
  }

  async fetchData(): Promise<void> {
    const user =
      this.props.userId != null
        ? unwrapOrThrow(await getUser(this.props.userId))
        : this.props.activeUser;

    // Use a cached version of this route to avoid that a tab switch in the dashboard
    // causes a whole-page spinner. Since the different tabs are controlled by the
    // router, the DashboardView re-mounts.
    const pricingPlanStatus = await cachedGetPricingPlanStatus();

    this.setState({
      user,
      pricingPlanStatus,
    });
  }

  uploadNmls = async (
    files: Array<File>,
    { createGroupForEachFile }: NmlImportOptions,
  ): Promise<void> => {
    const response = await Request.sendMultipartFormReceiveJSON("/api/annotations/upload", {
      data: {
        nmlFile: files,
        createGroupForEachFile,
      },
    });
    this.props.navigate(`/annotations/${response.annotation.id}`);
  };

  clearDatasetNameFilterFromUrl = () => {
    const tabKeyToURLMap = invert(urlTokenToTabKeyMap);
    const url = tabKeyToURLMap.annotations;
    this.props.navigate(`/dashboard/${url}`, { replace: false });
  };

  getValidTabKeys() {
    const { isAdminView } = this.props;
    return {
      publications: features().isWkorgInstance,
      datasets: !isAdminView,
      tasks: true,
      annotations: true,
    } as { [key: string]: boolean };
  }

  getTabs(user: APIUser): Tab[] {
    if (this.props.activeUser) {
      const validTabKeys = this.getValidTabKeys();
      const tabs = [
        validTabKeys.datasets
          ? {
              label: <span>Datasets</span>,
              key: "datasets",
              children: (
                <RenderingTabContext.Provider value="datasets">
                  <DatasetFolderView user={user} />
                </RenderingTabContext.Provider>
              ),
            }
          : null,
        {
          label: "Annotations",
          key: "annotations",
          children: (
            <RenderingTabContext.Provider value="annotations">
              <AnnotationsView
                isAdminView={this.props.isAdminView}
                userId={this.props.userId}
                activeUser={this.props.activeUser}
                datasetNameFilter={
                  getUrlParamsObjectFromString(this.props.location.search).dataset || null
                }
                onDatasetNameFilterCleared={this.clearDatasetNameFilterFromUrl}
              />
            </RenderingTabContext.Provider>
          ),
        },
        {
          label: "Tasks",
          key: "tasks",
          children: (
            <RenderingTabContext.Provider value="tasks">
              <DashboardTaskListView
                isAdminView={this.props.isAdminView}
                userId={this.props.userId}
              />
            </RenderingTabContext.Provider>
          ),
        },
        validTabKeys.publications
          ? {
              label: "Featured Publications",
              key: "publications",
              children: (
                <RenderingTabContext.Provider value="publications">
                  <PublicationViewWithHeader />
                </RenderingTabContext.Provider>
              ),
            }
          : null,
      ];

      return tabs.filter((el) => el != null) as Tab[];
    } else {
      return [];
    }
  }

  onDismissWelcomeBanner = () => {
    const [newUserSync] = updateNovelUserExperienceInfos(this.props.activeUser, {
      hasSeenDashboardWelcomeBanner: true,
    });
    this.props.updateActiveUser(newUserSync);
  };

  render() {
    const { user } = this.state;
    const { activeUser } = this.props;

    if (!user) {
      return (
        <div
          className="text-center"
          style={{
            marginTop: 50,
            width: "100vw",
          }}
        >
          <Spin size="large" />
        </div>
      );
    }

    const onTabChange = (activeTabKey: string) => {
      const tabKeyToURLMap = invert(urlTokenToTabKeyMap);

      const url = tabKeyToURLMap[activeTabKey];

      if (url) {
        UserLocalStorage.setItem("lastUsedDashboardTab", activeTabKey);

        if (!this.props.isAdminView) {
          this.props.navigate(`/dashboard/${url}`);
        }
      }

      this.setState({
        activeTabKey,
      });
    };

    const userHeader = this.props.isAdminView ? (
      <Typography.Title level={3}>
        User: {user.firstName} {user.lastName}
      </Typography.Title>
    ) : null;

    const whatsNextBanner =
      !this.props.isAdminView &&
      !activeUser.novelUserExperienceInfos.hasSeenDashboardWelcomeBanner ? (
        <WhatsNextHeader activeUser={activeUser} onDismiss={this.onDismissWelcomeBanner} />
      ) : null;
    this.state.pricingPlanStatus?.isAlmostExceeded;

    const pricingPlanWarnings =
      this.props.activeOrganization &&
      this.state.pricingPlanStatus?.isAlmostExceeded &&
      !this.state.pricingPlanStatus.isExceeded ? (
        <PlanAboutToExceedAlert organization={this.props.activeOrganization} />
      ) : null;
    const pricingPlanErrors =
      this.props.activeOrganization && this.state.pricingPlanStatus?.isExceeded ? (
        <PlanExceededAlert organization={this.props.activeOrganization} />
      ) : null;

    return (
      <NmlUploadZoneContainer onImport={this.uploadNmls} isUpdateAllowed>
        {whatsNextBanner}
        <div className="container propagate-flex-height" style={{ minHeight: "66vh" }}>
          {pricingPlanWarnings}
          {pricingPlanErrors}
          {userHeader}

          <ActiveTabContext.Provider value={this.state.activeTabKey}>
            <DashboardTabs
              activeKey={this.state.activeTabKey}
              onChange={onTabChange}
              items={this.getTabs(user)}
            />
          </ActiveTabContext.Provider>
        </div>
      </NmlUploadZoneContainer>
    );
  }
}

const mapStateToProps = (state: WebknossosState): StateProps => ({
  activeUser: enforceActiveUser(state.activeUser),
  activeOrganization: enforceActiveOrganization(state.activeOrganization),
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  updateActiveUser(activeUser: APIUser) {
    dispatch(setActiveUserAction(activeUser));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
export default connector(withRouter(DashboardView));
